import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDiskMetrics } from './useDynoMetrics';
import { formatBytes } from './BackupStatusWidget';
import { Deployment } from './monitoringTypes';

interface DatabaseSizeWidgetProps {
    viewerToken?: string;
    deployment?: Deployment;
}

const DatabaseSizeWidget: React.FC<DatabaseSizeWidgetProps> = ({ viewerToken, deployment }) => {
    const { t } = useTranslation();

    // Look back a few hours so the newest reading is always included
    const range = useMemo(() => ({
        from: new Date(Date.now() - 6 * 60 * 60 * 1000),
        to: new Date(),
    }), []);

    const { diskMetrics, loading } = useDiskMetrics(range, viewerToken, deployment);

    // Get the newest reading and the usage percentage of the quota
    const latest = diskMetrics.readings[diskMetrics.readings.length - 1];
    const sizeMb = latest ? latest.size_bytes / (1024 * 1024) : null;
    const percent = sizeMb !== null && diskMetrics.quota_mb ? Math.round((sizeMb / diskMetrics.quota_mb) * 100) : null;

    // Pick the status color based on the usage percentage
    let percentClass = 'admin-status-ok';
    if (percent !== null && percent > 95) percentClass = 'admin-status-critical';
    else if (percent !== null && percent > 80) percentClass = 'admin-status-warning';

    return (
        <div className='dashboard-card admin-status-widget'>
            <div className='admin-status-header'>
                <h6 className='title'>{t('dashboard.monitoring.admin.database.title')}</h6>
            </div>
            {loading ? (
                <div className='admin-status-loading'><div className='loader' /></div>
            ) : (
                <div className='admin-status-body'>
                    <div className={`admin-status-value ${percentClass}`}>
                        {percent !== null ? `${percent}%` : '-'}
                    </div>
                    <div className='admin-status-footer'>
                        <span className='admin-status-line-detail'>
                            {sizeMb !== null && diskMetrics.quota_mb
                                ? t('dashboard.monitoring.admin.database.of_quota', {
                                    size: formatBytes(latest!.size_bytes),
                                    quota: formatBytes(diskMetrics.quota_mb * 1024 * 1024),
                                })
                                : t('general.no_data_available')}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatabaseSizeWidget;
